import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, RotateCcw, CheckCircle2, XCircle, Upload, AlertTriangle, Layers, ShoppingBag, IndianRupee, Users, Crown, CreditCard, ArrowLeftRight, Plus, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDate, imgUrl } from "@/lib/api";
import { PageHeader, Pager, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MP = { amazon: "Amazon", flipkart: "Flipkart" };

function FilterBar({ filters, setFilters, agents, showStatus }) {
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      <select data-testid="filter-marketplace" value={filters.marketplace} onChange={(e) => setFilters({ ...filters, marketplace: e.target.value })}
        className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200">
        <option value="">All marketplaces</option><option value="amazon">Amazon</option><option value="flipkart">Flipkart</option>
      </select>
      {showStatus && (
        <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200">
          <option value="">All statuses</option>
          <option value="SELECTED">Pending</option>
          <option value="ADMIN_REVIEW">Processing</option>
          <option value="APPROVED">Approved</option>
          <option value="LISTED">Listed</option>
          <option value="REJECTED">Rejected</option>
        </select>
      )}
      <select data-testid="filter-agent" value={filters.agent_id} onChange={(e) => setFilters({ ...filters, agent_id: e.target.value })}
        className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200">
        <option value="">All agents</option>
        {(agents || []).map((a) => <option key={a.id} value={a.id}>{a.business_name}</option>)}
      </select>
      <Input data-testid="filter-search" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        placeholder="Search product / SKU…" className="w-48 border-slate-700 bg-slate-900 text-xs text-slate-200" />
      <Input data-testid="filter-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
        className="w-36 border-slate-700 bg-slate-900 text-xs text-slate-200" />
      <Input data-testid="filter-date-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
        className="w-36 border-slate-700 bg-slate-900 text-xs text-slate-200" />
    </div>
  );
}

const qs = (f) => `?marketplace=${f.marketplace}&agent_id=${f.agent_id}&status=${f.status || ""}&search=${encodeURIComponent(f.search || "")}&date_from=${f.date_from}&date_to=${f.date_to}`;

function DataTable({ testid, head, rows, empty }) {
  if (!rows?.length) return <EmptyState title={empty || "No records"} sub="Nothing here yet." />;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]">
      <table className="w-full text-left text-xs" data-testid={testid}>
        <thead><tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
          {head.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
        </tr></thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

// ---------- Marketplace Item Plans (admin) ----------
function PlansAdminTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mp-plans-admin"], queryFn: async () => (await api.get("/admin/marketplace/plans")).data });
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const openEdit = (p) => { setEdit(p || {}); setForm(p ? { name: p.name, item_limit: p.item_limit, monthly_price: p.monthly_price, trial_months: p.trial_months, status: p.status } : { name: "", item_limit: 25, monthly_price: 1000, trial_months: 3 }); };
  const save = useMutation({
    mutationFn: async () => edit?.id ? api.put(`/admin/marketplace/plans/${edit.id}`, form) : api.post("/admin/marketplace/plans", form),
    onSuccess: () => { toast.success("Plan saved — existing subscribers keep their current entitlement"); setEdit(null); qc.invalidateQueries({ queryKey: ["mp-plans-admin"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggle = useMutation({
    mutationFn: async (p) => api.put(`/admin/marketplace/plans/${p.id}`, { status: p.status === "active" ? "inactive" : "active" }),
    onSuccess: () => { toast.success("Plan status updated"); qc.invalidateQueries({ queryKey: ["mp-plans-admin"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div data-testid="mp-plans-tab">
      <div className="mb-3 flex justify-end">
        <Button size="sm" data-testid="mp-plan-create-btn" onClick={() => openEdit(null)} className="bg-[#FF6B00] text-white hover:bg-[#E85D04]"><Plus size={12} /> Create plan</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(data?.items || []).map((p) => (
          <div key={p.id} data-testid={`admin-mp-plan-${p.slug}`} className="rounded-xl border border-slate-800 bg-[#111827] p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-100">{p.name}</p>
              <StatusBadge status={p.status} />
            </div>
            <p className="mt-1 text-xl font-extrabold text-orange-400">{fmtINR(p.monthly_price)}<span className="text-[10px] font-medium text-slate-500">/month</span></p>
            <div className="mt-2 space-y-1 text-[11px] text-slate-400">
              <p>Item limit: <b className="text-slate-200">{p.item_limit} products</b></p>
              <p>First term: <b className="text-slate-200">{p.trial_months} months prepaid</b> (min), then monthly</p>
              <p data-testid={`plan-agents-${p.slug}`}>Agents: <b className="text-slate-200">{p.active_agents}</b> active · {p.trial_agents} legacy trial · {p.paid_agents} paid</p>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" data-testid={`edit-plan-${p.slug}`} onClick={() => openEdit(p)} className="h-7 flex-1 border-slate-700 text-[10px] text-slate-300 hover:bg-slate-800">Edit</Button>
              <Button size="sm" variant="outline" data-testid={`toggle-plan-${p.slug}`} onClick={() => toggle.mutate(p)}
                className={`h-7 text-[10px] ${p.status === "active" ? "border-rose-500/30 text-rose-400" : "border-emerald-500/30 text-emerald-400"}`}>
                {p.status === "active" ? "Deactivate" : "Activate"}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-slate-500">Editing a plan applies to future subscriptions only — agents already subscribed keep their current plan name, item limit and price (stored as a per-subscription snapshot).</p>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>{edit?.id ? `Edit ${edit.name}` : "Create marketplace plan"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name", "Plan name"], ["item_limit", "Item limit (unique products)"], ["monthly_price", "Monthly price ₹"], ["trial_months", "Minimum first term (months, prepaid)"]].map(([k, label]) => (
              <div key={k}>
                <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
                <Input data-testid={`plan-form-${k}`} type={k === "name" ? "text" : "number"} value={form[k] ?? ""}
                  onChange={(e) => setForm({ ...form, [k]: k === "name" ? e.target.value : e.target.value })}
                  className="border-slate-700 bg-slate-900 text-slate-100" />
              </div>
            ))}
            <Button data-testid="plan-save-btn" onClick={() => save.mutate({
              name: form.name, item_limit: Number(form.item_limit), monthly_price: Number(form.monthly_price),
              trial_months: Number(form.trial_months), ...(form.status ? { status: form.status } : {}),
            })} disabled={save.isPending || !form.name}
              className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">{save.isPending ? "Saving…" : "Save plan"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubscriptionsAdminTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const { data } = useQuery({
    queryKey: ["mp-subs-admin", status, page],
    queryFn: async () => (await api.get(`/admin/marketplace/subscriptions?status=${status}&page=${page}`)).data,
  });
  const { data: plansData } = useQuery({ queryKey: ["mp-plans-admin"], queryFn: async () => (await api.get("/admin/marketplace/plans")).data });
  const action = useMutation({
    mutationFn: async ({ id, body }) => (await api.put(`/admin/marketplace/subscriptions/${id}`, body)).data,
    onSuccess: () => { toast.success("Subscription updated"); qc.invalidateQueries({ queryKey: ["mp-subs-admin"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const FILTERS = [["", "All"], ["TRIAL", "Trial"], ["ACTIVE", "Active"], ["PAST_DUE", "Payment failed"], ["SUSPENDED", "Suspended"], ["CANCELLED", "Cancelled"]];
  return (
    <div data-testid="mp-subs-tab">
      <div className="mb-3 flex flex-wrap gap-1.5">
        {FILTERS.map(([v, l]) => (
          <button key={v} data-testid={`subs-filter-${v || "all"}`} onClick={() => { setStatus(v); setPage(1); }}
            className={`rounded-full px-3 py-1 text-[11px] font-semibold ${status === v ? "bg-[#FF6B00] text-white" : "border border-slate-700 text-slate-400 hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>
      {!data?.items?.length ? <EmptyState title="No subscriptions" sub="Agents who pick a Marketplace plan appear here." /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]">
          <table className="w-full text-left text-xs" data-testid="mp-subs-table">
            <thead><tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Agent</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Items</th>
              <th className="px-4 py-3">Trial end</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Next billing</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id} data-testid={`sub-row-${s.id}`} className="border-b border-slate-800/60">
                  <td className="px-4 py-2.5 text-slate-200">{s.agent_name}<br /><span className="text-[10px] text-slate-500">{s.agent_email}</span></td>
                  <td className="px-4 py-2.5 text-slate-300">{s.plan_snapshot?.name}</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-200">{s.items_used} / {s.plan_snapshot?.item_limit} <span className="text-[10px] text-slate-500">({s.items_available} free)</span></td>
                  <td className="px-4 py-2.5 text-slate-400">{s.trial_end ? fmtDate(s.trial_end) : "—"}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={s.subscription_status} /></td>
                  <td className="px-4 py-2.5 text-slate-300">{fmtINR(s.price)}/mo</td>
                  <td className="px-4 py-2.5 text-slate-400">{s.next_billing_date ? fmtDate(s.next_billing_date) : "—"}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={s.payment_status} /></td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {s.subscription_status !== "SUSPENDED" && s.subscription_status !== "CANCELLED" && (
                        <Button size="sm" variant="outline" data-testid={`sub-suspend-${s.id}`} onClick={() => action.mutate({ id: s.id, body: { action: "suspend" } })}
                          className="h-6 border-amber-500/30 px-2 text-[10px] text-amber-400 hover:bg-amber-500/10">Suspend</Button>
                      )}
                      {s.subscription_status === "SUSPENDED" && (
                        <Button size="sm" variant="outline" data-testid={`sub-reactivate-${s.id}`} onClick={() => action.mutate({ id: s.id, body: { action: "reactivate" } })}
                          className="h-6 border-emerald-500/30 px-2 text-[10px] text-emerald-400 hover:bg-emerald-500/10">Reactivate</Button>
                      )}
                      {s.subscription_status !== "CANCELLED" && (
                        <Button size="sm" variant="outline" data-testid={`sub-cancel-${s.id}`} onClick={() => action.mutate({ id: s.id, body: { action: "cancel" } })}
                          className="h-6 border-rose-500/30 px-2 text-[10px] text-rose-400 hover:bg-rose-500/10">Cancel</Button>
                      )}
                      <select data-testid={`sub-plan-${s.id}`} value="" onChange={(e) => e.target.value && action.mutate({ id: s.id, body: { action: "change_plan", plan_id: e.target.value } })}
                        className="h-6 rounded border border-slate-700 bg-slate-900 px-1 text-[10px] text-slate-300">
                        <option value="">Change plan…</option>
                        {(plansData?.items || []).filter((p) => p.id !== s.plan_id && p.status === "active").map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pager page={data.page} pages={data.pages} onPage={setPage} />}
    </div>
  );
}

function RevenueAdminTab() {
  const { data } = useQuery({ queryKey: ["mp-revenue-admin"], queryFn: async () => (await api.get("/admin/marketplace/revenue")).data });
  if (!data) return null;
  const cards = [
    ["Total subscribers", data.total_subscribers, "rev-total"],
    ["Trial agents", data.trial_agents, "rev-trial"],
    ["Paid agents", data.paid_agents, "rev-paid"],
    ["Past due", data.past_due, "rev-pastdue"],
    ["Suspended", data.suspended, "rev-suspended"],
    ["Monthly recurring revenue", fmtINR(data.mrr), "rev-mrr"],
    ["Collected to date", fmtINR(data.collected_total), "rev-collected"],
  ];
  return (
    <div data-testid="mp-revenue-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map(([l, v, tid]) => (
          <div key={l} className="rounded-xl border border-slate-800 bg-[#111827] p-4">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{l}</p>
            <p className="mt-1 text-lg font-extrabold text-slate-100" data-testid={tid}>{v}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-xl border border-slate-800 bg-[#111827] p-4">
        <p className="mb-2 text-xs font-bold text-slate-200">Plans in use</p>
        <div className="space-y-1.5">
          {Object.entries(data.by_plan || {}).map(([name, n]) => (
            <div key={name} className="flex justify-between border-b border-slate-800/60 py-1.5 text-xs" data-testid={`rev-plan-${name.replace(/\s+/g, "-").toLowerCase()}`}>
              <span className="text-slate-400">{name}</span><span className="font-semibold text-slate-200">{n} agents</span>
            </div>
          ))}
          {!Object.keys(data.by_plan || {}).length && <p className="text-xs text-slate-500">No active subscriptions yet.</p>}
        </div>
      </div>
      <p className="mt-3 text-[10px] text-slate-500">Figures come from real subscription + payment records only (Razorpay is in mock mode until live keys are configured).</p>
    </div>
  );
}

function SwapsAdminTab() {
  const [page, setPage] = useState(1);
  const { data } = useQuery({ queryKey: ["mp-swaps-admin", page], queryFn: async () => (await api.get(`/admin/marketplace/swaps?page=${page}`)).data });
  if (!data?.items?.length) return <EmptyState title="No swaps yet" sub="Agent product swaps appear here with full history." />;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]" data-testid="mp-swaps-tab">
      <table className="w-full text-left text-xs">
        <thead><tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
          <th className="px-4 py-3">Agent</th><th className="px-4 py-3">Released</th><th className="px-4 py-3">Selected</th><th className="px-4 py-3">Marketplace</th><th className="px-4 py-3">Date</th>
        </tr></thead>
        <tbody>
          {data.items.map((s) => (
            <tr key={s.id} data-testid={`swap-row-${s.id}`} className="border-b border-slate-800/60">
              <td className="px-4 py-2.5 text-slate-200">{s.agent_name}</td>
              <td className="px-4 py-2.5 text-rose-300">{s.old_product_name}</td>
              <td className="px-4 py-2.5 text-emerald-300">{s.new_product_name}</td>
              <td className="px-4 py-2.5 capitalize text-slate-400">{s.marketplace}</td>
              <td className="px-4 py-2.5 text-slate-500">{fmtDate(s.swapped_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-2"><Pager page={data.page} pages={data.pages} onPage={setPage} /></div>
    </div>
  );
}

function ReleasesAdminTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mp-releases-admin"], queryFn: async () => (await api.get("/admin/marketplace/release-requests")).data });
  const act = useMutation({
    mutationFn: async ({ id, action }) => api.post(`/admin/marketplace/release-requests/${id}/action`, { action }),
    onSuccess: () => { toast.success("Listing decision recorded"); qc.invalidateQueries({ queryKey: ["mp-releases-admin"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const items = data?.items || [];
  if (!items.length) return <EmptyState title="No release requests" sub="When an agent releases a product with live listings, you decide what happens to the marketplace listing here." />;
  return (
    <div className="space-y-3" data-testid="mp-releases-tab">
      {items.map((r) => (
        <div key={r.id} data-testid={`release-req-${r.id}`} className="rounded-xl border border-slate-800 bg-[#111827] p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-slate-100">{r.product_name}</p>
              <p className="text-[11px] text-slate-500">{r.agent_name} · {r.reason} · {fmtDate(r.created_at)}</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {(r.listings || []).map((l) => (
                  <span key={l.listing_id} className="rounded-full border border-slate-700 px-2 py-0.5 font-mono text-[9px] text-slate-400">
                    {l.marketplace} · {l.marketplace_sku} · {l.listing_status}
                  </span>
                ))}
              </div>
            </div>
            {r.action === "pending" ? (
              <div className="flex gap-1.5">
                {[["deactivate", "Deactivate"], ["remove", "Remove"], ["keep", "Keep active"]].map(([a, l]) => (
                  <Button key={a} size="sm" data-testid={`release-action-${a}-${r.id}`} onClick={() => act.mutate({ id: r.id, action: a })} disabled={act.isPending}
                    className={`h-7 px-2.5 text-[10px] font-bold ${a === "keep" ? "bg-emerald-600 text-white hover:bg-emerald-500" : a === "remove" ? "bg-rose-600 text-white hover:bg-rose-500" : "bg-amber-500 text-black hover:bg-amber-400"}`}>{l}</Button>
                ))}
              </div>
            ) : (
              <StatusBadge status={r.action} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Marketplace Request Detail (admin) ----------
function RequestDetailDialog({ sid, onClose, onAction, busy }) {
  const [note, setNote] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["mp-req-detail", sid],
    queryFn: async () => (await api.get(`/admin/marketplace/selections/${sid}/detail`)).data,
    enabled: !!sid,
  });
  const s = data?.selection;
  const p = data?.product;
  const a = data?.agent;
  return (
    <Dialog open={!!sid} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-slate-800 bg-[#0B1220] text-slate-100">
        <DialogHeader><DialogTitle>Marketplace Request {s ? `· ${MP[s.marketplace]}` : ""}</DialogTitle></DialogHeader>
        {isLoading || !data ? <p className="text-xs text-slate-500">Loading…</p> : (
          <div className="space-y-4 text-xs" data-testid="request-detail">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-800 bg-[#111827] p-4">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Agent</p>
                <p className="text-sm font-bold text-slate-100" data-testid="req-agent-name">{a?.business_name || a?.name}</p>
                <p className="mt-0.5 font-mono text-[11px] font-semibold text-orange-300" data-testid="req-agent-code">{a?.agent_code || "—"}</p>
                <p className="mt-1 text-slate-400">{a?.name} · {a?.email}</p>
                <p className="text-slate-500">{a?.mobile}</p>
                <p className="mt-1 font-mono text-[9px] text-slate-600">Agent ID: {a?.id}</p>
              </div>
              <div className="rounded-xl border border-slate-800 bg-[#111827] p-4">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Request</p>
                <div className="space-y-1 text-slate-300">
                  <div className="flex justify-between"><span className="text-slate-500">Marketplace</span><b>{MP[s.marketplace]}</b></div>
                  <div className="flex justify-between"><span className="text-slate-500">Selling price</span><b className="text-emerald-400" data-testid="req-price">{fmtINR(s.suggested_price || 0)}</b></div>
                  <div className="flex justify-between"><span className="text-slate-500">Requested</span><span>{fmtDate(s.selected_at)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Status</span><StatusBadge status={s.selection_status} /></div>
                </div>
                {s.review_note && <p className="mt-2 rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[10px] text-rose-300" data-testid="req-review-note">Note: {s.review_note}</p>}
                {data.listing && <p className="mt-2 font-mono text-[9px] text-slate-500">Listing: {data.listing.marketplace_sku} · {data.listing.listing_status}</p>}
              </div>
            </div>
            {p && (
              <div className="rounded-xl border border-slate-800 bg-[#111827] p-4">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Product</p>
                <div className="flex gap-3">
                  {(p.images || [])[0] && <img src={imgUrl(p.images[0])} alt={p.name} className="h-16 w-16 rounded-lg object-cover" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-100" data-testid="req-product-name">{p.name}</p>
                    <p className="font-mono text-[10px] text-slate-500">{p.sku} · {p.category || "—"} {p.brand ? `· ${p.brand}` : ""}</p>
                    <p className="mt-1 text-[10px] text-slate-400">MRP {fmtINR(p.mrp || 0)} · Partner price {fmtINR(p.agent_cost || 0)} · Stock {p.stock ?? 0}</p>
                    {p.short_description && <p className="mt-1 line-clamp-2 text-[11px] text-slate-400">{p.short_description}</p>}
                  </div>
                </div>
                {(p.images || []).length > 1 && (
                  <div className="mt-2 flex gap-1.5 overflow-x-auto">
                    {p.images.slice(1, 5).map((im, i) => <img key={i} src={imgUrl(im)} alt="" className="h-10 w-10 rounded object-cover" />)}
                  </div>
                )}
              </div>
            )}
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Admin note (required for rejection — shared with agent)</p>
              <Input data-testid="req-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Missing required product information" className="border-slate-700 bg-slate-900 text-slate-100" />
            </div>
            <div className="flex flex-wrap gap-2">
              {s.selection_status === "SELECTED" && (
                <Button data-testid="req-process-btn" onClick={() => onAction({ status: "PROCESSING", note })} disabled={busy}
                  className="bg-blue-600 text-xs font-semibold text-white hover:bg-blue-500">Mark Processing</Button>
              )}
              {["SELECTED", "ADMIN_REVIEW", "REJECTED"].includes(s.selection_status) && (
                <Button data-testid="req-approve-btn" onClick={() => onAction({ status: "APPROVED", note })} disabled={busy}
                  className="bg-emerald-600 text-xs font-semibold text-white hover:bg-emerald-500">Approve</Button>
              )}
              {["SELECTED", "ADMIN_REVIEW", "APPROVED"].includes(s.selection_status) && (
                <Button data-testid="req-reject-btn" onClick={() => onAction({ status: "REJECTED", note })} disabled={busy || !note.trim()}
                  className="bg-rose-600 text-xs font-semibold text-white hover:bg-rose-500">Reject</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MarketplaceAdmin() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ marketplace: "", agent_id: "", status: "", search: "", date_from: "", date_to: "" });
  const [selPage, setSelPage] = useState(1);
  const [viewFor, setViewFor] = useState(null);
  const [publishFor, setPublishFor] = useState(null);
  const [publishPrice, setPublishPrice] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [releaseFor, setReleaseFor] = useState(null);
  const [syncMarketplace, setSyncMarketplace] = useState("");

  const { data: overview } = useQuery({ queryKey: ["mp-admin-overview"], queryFn: async () => (await api.get("/admin/marketplace/overview")).data });
  const { data: agentsData } = useQuery({ queryKey: ["agents-mini"], queryFn: async () => (await api.get("/admin/agents?limit=100")).data });
  const { data: selections } = useQuery({
    queryKey: ["mp-selections", filters, selPage],
    queryFn: async () => (await api.get(`/admin/marketplace/selections${qs(filters)}&page=${selPage}`)).data,
  });
  const { data: allocations } = useQuery({ queryKey: ["mp-allocations"], queryFn: async () => (await api.get("/admin/marketplace/allocations")).data });
  const { data: listings } = useQuery({ queryKey: ["mp-admin-listings", filters], queryFn: async () => (await api.get(`/admin/marketplace/listings${qs(filters)}`)).data });
  const { data: orders } = useQuery({ queryKey: ["mp-admin-orders", filters], queryFn: async () => (await api.get(`/admin/marketplace/orders${qs(filters)}`)).data });
  const { data: shipments } = useQuery({ queryKey: ["mp-admin-shipments", filters], queryFn: async () => (await api.get(`/admin/marketplace/shipments${qs(filters)}`)).data });
  const { data: returns } = useQuery({ queryKey: ["mp-admin-returns", filters], queryFn: async () => (await api.get(`/admin/marketplace/returns${qs(filters)}`)).data });
  const { data: sales } = useQuery({ queryKey: ["mp-admin-sales", filters], queryFn: async () => (await api.get(`/admin/marketplace/sales${qs(filters)}`)).data });
  const { data: syncLogs, refetch: refetchSync } = useQuery({ queryKey: ["mp-sync-logs"], queryFn: async () => (await api.get("/admin/marketplace/sync-logs")).data });
  const { data: errors, refetch: refetchErrors } = useQuery({ queryKey: ["mp-errors"], queryFn: async () => (await api.get("/admin/marketplace/errors")).data });
  const { data: configStatus } = useQuery({ queryKey: ["mp-config-status"], queryFn: async () => (await api.get("/admin/marketplace/config-status")).data });

  const refreshAll = () => qc.invalidateQueries();

  const setStatus = useMutation({
    mutationFn: async ({ sid, status, note }) => (await api.post(`/admin/marketplace/selections/${sid}/status`, { status, note })).data,
    onSuccess: (d) => { toast.success(`Status updated — ${d.status === "ADMIN_REVIEW" ? "Processing" : d.status}`); refreshAll(); qc.invalidateQueries({ queryKey: ["mp-req-detail"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const approve = useMutation({
    mutationFn: async (sid) => api.post(`/admin/marketplace/selections/${sid}/approve`, {}),
    onSuccess: () => { toast.success("Selection approved"); refreshAll(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const reject = useMutation({
    mutationFn: async () => api.post(`/admin/marketplace/selections/${rejectFor.id}/reject`, { reason: rejectReason }),
    onSuccess: () => { toast.success("Rejected — slot released if no other selection remains"); setRejectFor(null); setRejectReason(""); refreshAll(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const publish = useMutation({
    mutationFn: async () => (await api.post("/admin/marketplace/publish", { selection_id: publishFor.id, price: Number(publishPrice) || undefined })).data,
    onSuccess: (d) => { toast.success(`Listing LIVE — ${d.marketplace_sku}`); setPublishFor(null); refreshAll(); },
    onError: (e) => { toast.error(errMsg(e)); refreshAll(); },
  });
  const retryListing = useMutation({
    mutationFn: async (lid) => (await api.post(`/admin/marketplace/listings/${lid}/retry`, {})).data,
    onSuccess: () => { toast.success("Retry succeeded — listing live"); refreshAll(); refetchErrors(); },
    onError: (e) => { toast.error(errMsg(e)); refreshAll(); refetchErrors(); },
  });
  const removeListing = useMutation({
    mutationFn: async (lid) => api.delete(`/admin/marketplace/listings/${lid}`),
    onSuccess: () => { toast.success("Listing removed"); refreshAll(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const releaseSlot = useMutation({
    mutationFn: async () => api.post("/admin/marketplace/allocations/release", { product_id: releaseFor.product_id, agent_id: releaseFor.agent_id }),
    onSuccess: () => { toast.success("Allocation released"); setReleaseFor(null); refreshAll(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const syncNow = useMutation({
    mutationFn: async () => (await api.post("/admin/marketplace/sync-now", { marketplace: syncMarketplace || undefined })).data,
    onSuccess: (d) => {
      d.status === "SYNCED" ? toast.success(`Sync complete — ${d.synced} account(s)`) : toast.warning(d.message);
      refetchSync();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const cards = [
    { icon: Users, label: "Pending selections", value: overview?.pending_selections ?? 0, testid: "ov-pending" },
    { icon: CheckCircle2, label: "Approved", value: overview?.approved ?? 0, testid: "ov-approved" },
    { icon: Layers, label: "Active listings", value: overview?.active_listings ?? 0, testid: "ov-active-listings" },
    { icon: AlertTriangle, label: "Failed listings", value: overview?.failed_listings ?? 0, testid: "ov-failed" },
    { icon: ShoppingBag, label: "Orders", value: overview?.orders ?? 0, testid: "ov-orders" },
    { icon: IndianRupee, label: "Gross sales", value: fmtINR(overview?.gross_sales ?? 0), testid: "ov-gross" },
    { icon: IndianRupee, label: "Net sales", value: fmtINR(overview?.net_sales ?? 0), testid: "ov-net" },
    { icon: RotateCcw, label: "Returns", value: overview?.returns ?? 0, testid: "ov-returns" },
  ];

  const listingRows = (items) => items?.map((l) => (
    <tr key={l.id} data-testid={`admin-listing-${l.id}`} className="border-b border-slate-800/60">
      <td className="px-4 py-2.5 capitalize">{l.marketplace}</td>
      <td className="px-4 py-2.5 text-slate-300">{l.agent_name}</td>
      <td className="px-4 py-2.5 text-slate-300">{l.product_name}<span className="ml-1 font-mono text-[9px] text-slate-500">{l.sku}</span></td>
      <td className="px-4 py-2.5 font-mono text-[10px] text-slate-400">{l.marketplace_sku}</td>
      <td className="px-4 py-2.5 font-mono text-[10px] text-slate-400">{l.marketplace_listing_id || "—"}</td>
      <td className="px-4 py-2.5">{fmtINR(l.marketplace_price || 0)}</td>
      <td className="px-4 py-2.5">{l.marketplace_quantity ?? "—"}</td>
      <td className="px-4 py-2.5"><StatusBadge status={l.listing_status} />{l.error_message && <p className="mt-0.5 max-w-48 truncate text-[9px] text-rose-400" title={l.error_message}>{l.error_message}</p>}</td>
      <td className="px-4 py-2.5 text-slate-500">{l.last_synced_at ? fmtDate(l.last_synced_at) : "—"}</td>
      <td className="px-4 py-2.5">
        <div className="flex gap-1.5">
          {l.listing_status === "ERROR" && (
            <Button data-testid={`retry-listing-${l.id}`} size="sm" onClick={() => retryListing.mutate(l.id)} disabled={retryListing.isPending}
              className="h-6 bg-amber-500 px-2 text-[10px] font-bold text-white hover:bg-amber-600">Retry</Button>
          )}
          {["ACTIVE", "ERROR"].includes(l.listing_status) && (
            <Button data-testid={`remove-listing-${l.id}`} size="sm" variant="outline" onClick={() => removeListing.mutate(l.id)}
              className="h-6 border-rose-500/30 px-2 text-[10px] text-rose-400 hover:bg-rose-500/10">Remove</Button>
          )}
        </div>
      </td>
    </tr>
  ));

  return (
    <div data-testid="admin-marketplace-page">
      <PageHeader title="Marketplace Control Center" sub="Agent selections → admin approval → admin publishing → real marketplace sync. Amazon & Flipkart only." testid="admin-marketplace-title">
        <Button data-testid="sync-now-btn" onClick={() => syncNow.mutate()} disabled={syncNow.isPending}
          className="bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
          <RefreshCw size={14} className={syncNow.isPending ? "animate-spin" : ""} /> Sync Now
        </Button>
      </PageHeader>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap border border-slate-800 bg-[#111827]">
          {[["overview", "Overview"], ["selections", "Agent Selections"], ["plans", "Plans"], ["subscriptions", "Subscriptions"], ["revenue", "Revenue"],
            ["swaps", "Swaps"], ["releases", "Releases"], ["amazon", "Amazon"], ["flipkart", "Flipkart"],
            ["orders", "Orders"], ["shipments", "Shipments"], ["returns", "Returns"], ["sales", "Sales"],
            ["sync", "Synchronization"], ["errors", "Errors / Logs"], ["settings", "Settings"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="plans" className="mt-4"><PlansAdminTab /></TabsContent>
        <TabsContent value="subscriptions" className="mt-4"><SubscriptionsAdminTab /></TabsContent>
        <TabsContent value="revenue" className="mt-4"><RevenueAdminTab /></TabsContent>
        <TabsContent value="swaps" className="mt-4"><SwapsAdminTab /></TabsContent>
        <TabsContent value="releases" className="mt-4"><ReleasesAdminTab /></TabsContent>

        <TabsContent value="overview" className="mt-4">
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
            {cards.map((c) => (
              <div key={c.label} data-testid={c.testid} className="rounded-xl border border-slate-800 bg-[#111827] p-3">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
                <p className="mt-1 text-lg font-extrabold text-slate-50">{c.value}</p>
              </div>
            ))}
          </div>
          <p className="mb-2 text-[11px] text-slate-500" data-testid="ov-last-sync">
            Last sync: {overview?.last_sync ? fmtDate(overview.last_sync) : "never"} {overview?.last_sync_status && `· ${overview.last_sync_status}`} · Connected accounts: {overview?.accounts_connected ?? 0}
          </p>

          <h3 className="mb-3 mt-6 text-sm font-semibold text-slate-200">Product Allocation (max {allocations?.max_agents ?? 5} unique agents per product)</h3>
          <DataTable testid="allocations-table" empty="No marketplace allocations yet"
            head={["Product", "SKU", "Slots", "Agents", ""]}
            rows={allocations?.items?.map((a) => (
              <tr key={a.product_id} data-testid={`allocation-${a.sku}`} className="border-b border-slate-800/60">
                <td className="px-4 py-2.5 text-slate-200">{a.product_name}</td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-slate-500">{a.sku}</td>
                <td className="px-4 py-2.5">
                  <span data-testid={`slots-used-${a.sku}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${a.full ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400"}`}>
                    {a.slots_used}/{a.slots_total}{a.full ? " FULL" : ` · ${a.slots_available} open`}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-[10px] text-slate-400">
                  {a.agents.map((ag, i) => (
                    <span key={ag.agent_id} className="mr-2 inline-block">
                      {i + 1}. {ag.agent_name} ({Object.entries(ag.marketplaces).map(([m, s]) => `${MP[m]}:${s}`).join(", ") || "—"})
                      <button data-testid={`release-${a.sku}-${ag.agent_id}`} onClick={() => setReleaseFor({ product_id: a.product_id, agent_id: ag.agent_id, name: ag.agent_name, product: a.product_name })}
                        className="ml-1 text-rose-400 hover:underline">release</button>
                    </span>
                  ))}
                </td>
                <td className="px-4 py-2.5">{a.full && <span className="text-[10px] font-bold text-rose-400">FULL</span>}</td>
              </tr>
            ))} />
        </TabsContent>

        <TabsContent value="selections" className="mt-4">
          <FilterBar filters={filters} setFilters={(f) => { setFilters(f); setSelPage(1); }} agents={agentsData?.items} showStatus />
          <DataTable testid="selections-table" empty="No agent marketplace requests yet"
            head={["Agent", "Agent Code", "Marketplace", "Product", "SKU", "Selling Price", "Requested", "Status", "Listing", "Actions"]}
            rows={selections?.items?.map((s) => (
              <tr key={s.id} data-testid={`selection-${s.id}`} className="border-b border-slate-800/60">
                <td className="px-4 py-2.5 text-slate-300">{s.agent_name}<span className="block font-mono text-[9px] text-slate-600">{s.agent_id.slice(0, 8)}</span></td>
                <td className="px-4 py-2.5 font-mono text-[10px] font-semibold text-orange-300" data-testid={`agent-code-${s.id}`}>{s.agent_code || "—"}</td>
                <td className="px-4 py-2.5 capitalize">{s.marketplace}</td>
                <td className="px-4 py-2.5 text-slate-200">{s.product_name}</td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-slate-500">{s.sku}</td>
                <td className="px-4 py-2.5">{fmtINR(s.suggested_price || 0)}</td>
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(s.selected_at)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={s.selection_status} />{s.review_note && <p className="mt-0.5 text-[9px] text-rose-400">{s.review_note}</p>}</td>
                <td className="px-4 py-2.5 text-[10px] text-slate-400">{s.listing ? <>{s.listing.status}<span className="block font-mono text-[9px]">{s.listing.marketplace_sku}</span></> : "—"}</td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-1.5">
                    <Button data-testid={`view-${s.id}`} size="sm" variant="outline" onClick={() => setViewFor(s.id)}
                      className="h-6 border-slate-600 px-2 text-[10px] text-slate-300 hover:bg-slate-800">View</Button>
                    {s.selection_status === "SELECTED" && (
                      <Button data-testid={`process-${s.id}`} size="sm" variant="outline" onClick={() => setStatus.mutate({ sid: s.id, status: "PROCESSING" })} disabled={setStatus.isPending}
                        className="h-6 border-blue-500/30 px-2 text-[10px] text-blue-400 hover:bg-blue-500/10">Processing</Button>
                    )}
                    {["SELECTED", "ADMIN_REVIEW"].includes(s.selection_status) && (
                      <>
                        <Button data-testid={`approve-${s.id}`} size="sm" onClick={() => approve.mutate(s.id)} disabled={approve.isPending}
                          className="h-6 bg-emerald-500 px-2 text-[10px] font-bold text-white hover:bg-emerald-600"><CheckCircle2 size={10} /> Approve</Button>
                        <Button data-testid={`reject-${s.id}`} size="sm" variant="outline" onClick={() => setRejectFor(s)}
                          className="h-6 border-rose-500/30 px-2 text-[10px] text-rose-400 hover:bg-rose-500/10"><XCircle size={10} /> Reject</Button>
                      </>
                    )}
                    {["APPROVED", "FAILED"].includes(s.selection_status) && (
                      <Button data-testid={`publish-${s.id}`} size="sm" onClick={() => { setPublishFor(s); setPublishPrice(String(s.suggested_price || "")); }}
                        className="h-6 bg-[#FF6B00] px-2 text-[10px] font-bold text-white hover:bg-[#E85D04]"><Upload size={10} /> Publish</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))} />
          {selections && <Pager page={selections.page} pages={selections.pages} onPage={setSelPage} />}
        </TabsContent>

        {["amazon", "flipkart"].map((ch) => (
          <TabsContent key={ch} value={ch} className="mt-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs text-slate-400">
                {MP[ch]} account config: <b className={configStatus?.[ch]?.configured ? "text-emerald-400" : "text-amber-400"}>
                  {configStatus?.[ch]?.configured ? `Configured (${configStatus[ch].mode}, ${configStatus[ch].source})` : "Not configured — connect in Agent Detail"}
                </b>
              </p>
              <Button data-testid={`sync-${ch}-btn`} size="sm" variant="outline" onClick={() => { setSyncMarketplace(ch); setTimeout(() => syncNow.mutate(), 0); }}
                disabled={syncNow.isPending} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <RefreshCw size={11} /> Sync {MP[ch]}
              </Button>
            </div>
            <DataTable testid={`${ch}-listings-table`} empty={`No ${MP[ch]} listings yet`}
              head={["Marketplace", "Agent", "Product", "MP SKU", "Listing ID", "Price", "Qty", "Status", "Last Sync", "Actions"]}
              rows={listingRows((listings?.items || []).filter((l) => l.marketplace === ch))} />
          </TabsContent>
        ))}

        <TabsContent value="orders" className="mt-4">
          <FilterBar filters={filters} setFilters={setFilters} agents={agentsData?.items} />
          <DataTable testid="admin-mp-orders-table" empty="No marketplace orders synced"
            head={["Order ID", "Agent", "Marketplace", "Date", "Items", "Amount", "Status"]}
            rows={orders?.items?.map((o) => (
              <tr key={o.id} className="border-b border-slate-800/60">
                <td className="px-4 py-2.5 font-mono text-[10px] text-slate-300">{o.marketplace_order_id}</td>
                <td className="px-4 py-2.5 text-slate-300">{o.agent_name}</td>
                <td className="px-4 py-2.5 capitalize">{o.marketplace}</td>
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(o.order_date)}</td>
                <td className="px-4 py-2.5 text-slate-400">{(o.items || []).map((i) => `${i.marketplace_sku} ×${i.quantity}`).join(", ") || "—"}</td>
                <td className="px-4 py-2.5 font-semibold text-slate-100">{fmtINR(o.total_amount)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={o.order_status} /></td>
              </tr>
            ))} />
        </TabsContent>

        <TabsContent value="shipments" className="mt-4">
          <FilterBar filters={filters} setFilters={setFilters} agents={agentsData?.items} />
          <DataTable testid="admin-mp-shipments-table" empty="No shipments synced"
            head={["Agent", "Tracking", "Courier", "Status", "Shipped", "Delivered", "Last Sync"]}
            rows={shipments?.items?.map((s) => (
              <tr key={s.id} className="border-b border-slate-800/60">
                <td className="px-4 py-2.5 text-slate-300">{s.agent_name}</td>
                <td className="px-4 py-2.5 font-mono text-[10px]">{s.tracking_number || "—"}</td>
                <td className="px-4 py-2.5">{s.courier || "—"}</td>
                <td className="px-4 py-2.5"><StatusBadge status={s.shipment_status} /></td>
                <td className="px-4 py-2.5 text-slate-500">{s.shipped_at ? fmtDate(s.shipped_at) : "—"}</td>
                <td className="px-4 py-2.5 text-slate-500">{s.delivered_at ? fmtDate(s.delivered_at) : "—"}</td>
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(s.last_synced_at)}</td>
              </tr>
            ))} />
        </TabsContent>

        <TabsContent value="returns" className="mt-4">
          <FilterBar filters={filters} setFilters={setFilters} agents={agentsData?.items} />
          <DataTable testid="admin-mp-returns-table" empty="No returns synced"
            head={["Return ID", "Agent", "Marketplace", "Reason", "Refund", "Status", "Date"]}
            rows={returns?.items?.map((r) => (
              <tr key={r.id} className="border-b border-slate-800/60">
                <td className="px-4 py-2.5 font-mono text-[10px] text-slate-300">{r.return_id}</td>
                <td className="px-4 py-2.5 text-slate-300">{r.agent_name}</td>
                <td className="px-4 py-2.5 capitalize">{r.marketplace}</td>
                <td className="px-4 py-2.5 text-slate-400">{r.return_reason || "—"}</td>
                <td className="px-4 py-2.5">{r.refund_amount ? fmtINR(r.refund_amount) : "—"}</td>
                <td className="px-4 py-2.5"><StatusBadge status={r.return_status} /></td>
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(r.created_at)}</td>
              </tr>
            ))} />
        </TabsContent>

        <TabsContent value="sales" className="mt-4">
          <FilterBar filters={filters} setFilters={setFilters} agents={agentsData?.items} />
          <DataTable testid="admin-mp-sales-table" empty="No sales synced"
            head={["Date", "Agent", "Marketplace", "Qty", "Gross", "Fees", "Refund", "Net"]}
            rows={sales?.items?.map((s) => (
              <tr key={s.id} className="border-b border-slate-800/60">
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(s.sale_date)}</td>
                <td className="px-4 py-2.5 text-slate-300">{s.agent_name}</td>
                <td className="px-4 py-2.5 capitalize">{s.marketplace}</td>
                <td className="px-4 py-2.5">{s.quantity}</td>
                <td className="px-4 py-2.5">{fmtINR(s.gross_amount)}</td>
                <td className="px-4 py-2.5 text-slate-500">{fmtINR(s.fees || 0)}</td>
                <td className="px-4 py-2.5 text-slate-500">{fmtINR(s.refund_amount || 0)}</td>
                <td className="px-4 py-2.5 font-semibold text-emerald-400">{fmtINR(s.net_amount)}</td>
              </tr>
            ))} />
        </TabsContent>

        <TabsContent value="sync" className="mt-4">
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-800 bg-[#111827] p-4">
            <select data-testid="sync-marketplace-select" value={syncMarketplace} onChange={(e) => setSyncMarketplace(e.target.value)}
              className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-slate-200">
              <option value="">All marketplaces</option><option value="amazon">Amazon</option><option value="flipkart">Flipkart</option>
            </select>
            <Button data-testid="run-sync-btn" onClick={() => syncNow.mutate()} disabled={syncNow.isPending}
              className="bg-[#FF6B00] text-xs font-semibold text-white hover:bg-[#E85D04]">
              <RefreshCw size={12} className={syncNow.isPending ? "animate-spin" : ""} /> Run sync now
            </Button>
            <p className="text-[10px] text-slate-500">Listings, orders, shipments, returns & inventory sync hourly via the background scheduler for connected accounts.</p>
          </div>
          <DataTable testid="sync-logs-table" empty="No sync runs yet"
            head={["Time", "Marketplace", "Agent", "Type", "Status", "Records", "Error"]}
            rows={syncLogs?.items?.map((l) => (
              <tr key={l.id} data-testid={`sync-log-${l.id}`} className="border-b border-slate-800/60">
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(l.created_at)}</td>
                <td className="px-4 py-2.5 capitalize">{l.marketplace}</td>
                <td className="px-4 py-2.5 text-slate-300">{l.agent_name || "—"}</td>
                <td className="px-4 py-2.5">{l.sync_type}</td>
                <td className="px-4 py-2.5"><StatusBadge status={l.status} /></td>
                <td className="px-4 py-2.5">{l.records_processed}</td>
                <td className="max-w-56 truncate px-4 py-2.5 text-[10px] text-rose-400" title={l.error_message}>{l.error_message || "—"}</td>
              </tr>
            ))} />
        </TabsContent>

        <TabsContent value="errors" className="mt-4">
          <DataTable testid="errors-table" empty="No marketplace errors"
            head={["Date", "Marketplace", "Agent", "Product", "Operation", "Code", "Message", ""]}
            rows={errors?.items?.map((e, i) => (
              <tr key={e.id || i} className="border-b border-slate-800/60">
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(e.created_at || e.updated_at)}</td>
                <td className="px-4 py-2.5 capitalize">{e.marketplace}</td>
                <td className="px-4 py-2.5 text-slate-300">{e.agent_name || "—"}</td>
                <td className="px-4 py-2.5 text-slate-300">{e.product_name || "—"}<span className="ml-1 font-mono text-[9px] text-slate-600">{e.sku}</span></td>
                <td className="px-4 py-2.5">{e.operation}</td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-amber-400">{e.error_code}</td>
                <td className="max-w-56 truncate px-4 py-2.5 text-[10px] text-rose-400" title={e.error_message}>{e.error_message}</td>
                <td className="px-4 py-2.5">
                  {e.listing_id && (
                    <Button data-testid={`error-retry-${e.listing_id}`} size="sm" onClick={() => retryListing.mutate(e.listing_id)} disabled={retryListing.isPending}
                      className="h-6 bg-amber-500 px-2 text-[10px] font-bold text-white hover:bg-amber-600">Retry</Button>
                  )}
                </td>
              </tr>
            ))} />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {["amazon", "flipkart"].map((ch) => (
              <div key={ch} data-testid={`settings-${ch}`} className="rounded-xl border border-slate-800 bg-[#111827] p-5">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-100">{MP[ch]}</h3>
                  <StatusBadge status={configStatus?.[ch]?.configured ? "configured" : "not_configured"} />
                </div>
                <div className="space-y-1.5 text-xs text-slate-400">
                  <div className="flex justify-between"><span>App credentials</span><span className="capitalize text-slate-300">{configStatus?.[ch]?.source || "none"}</span></div>
                  <div className="flex justify-between"><span>Mode</span><span className="capitalize text-slate-300">{configStatus?.[ch]?.mode || "—"}</span></div>
                  <div className="flex justify-between"><span>Seller authorization</span><span className="text-slate-300">Per agent — Agent Detail → Marketplace</span></div>
                </div>
                <p className="mt-3 rounded-lg border border-slate-700 bg-slate-900/50 px-3 py-2 text-[10px] text-slate-500">
                  Credentials & seller OAuth are managed per agent in <b>Agents → Agent Detail → Marketplace</b>. Secrets are encrypted server-side and never shown to agents.
                </p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <RequestDetailDialog sid={viewFor} onClose={() => setViewFor(null)} busy={setStatus.isPending}
        onAction={({ status, note }) => setStatus.mutate({ sid: viewFor, status, note }, { onSuccess: () => setViewFor(null) })} />

      <Dialog open={!!publishFor} onOpenChange={() => setPublishFor(null)}>
        <DialogContent className="border-slate-800 bg-[#0B1220] text-slate-100">
          <DialogHeader><DialogTitle>Publish to {MP[publishFor?.marketplace]}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">{publishFor?.product_name} ({publishFor?.sku}) for agent <b>{publishFor?.agent_name}</b></p>
          <div>
            <p className="text-xs text-slate-500">Marketplace price (agent suggested {fmtINR(publishFor?.suggested_price || 0)})</p>
            <Input data-testid="publish-price-input" type="number" value={publishPrice} onChange={(e) => setPublishPrice(e.target.value)}
              className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
          </div>
          <Button data-testid="publish-confirm-btn" onClick={() => publish.mutate()} disabled={publish.isPending}
            className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">
            {publish.isPending ? "Publishing…" : `Publish to ${MP[publishFor?.marketplace]}`}
          </Button>
          <p className="text-[10px] text-slate-500">Status becomes ACTIVE only after the marketplace API accepts the listing. Failures are logged with the real error.</p>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectFor} onOpenChange={() => setRejectFor(null)}>
        <DialogContent className="border-slate-800 bg-[#0B1220] text-slate-100">
          <DialogHeader><DialogTitle>Reject selection</DialogTitle></DialogHeader>
          <Input data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (shared with agent)" className="border-slate-700 bg-slate-900 text-slate-100" />
          <Button data-testid="reject-confirm-btn" onClick={() => reject.mutate()} disabled={reject.isPending}
            className="w-full bg-rose-500 font-semibold text-white hover:bg-rose-600">Reject</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!releaseFor} onOpenChange={() => setReleaseFor(null)}>
        <DialogContent className="border-slate-800 bg-[#0B1220] text-slate-100">
          <DialogHeader><DialogTitle>Release allocation slot?</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">
            This removes <b>{releaseFor?.name}</b>'s marketplace allocation for <b>{releaseFor?.product}</b> on all marketplaces, removes their listings, and frees 1 slot for another agent.
          </p>
          <Button data-testid="release-confirm-btn" onClick={() => releaseSlot.mutate()} disabled={releaseSlot.isPending}
            className="w-full bg-rose-500 font-semibold text-white hover:bg-rose-600">Confirm release</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
